import React, { useEffect, useState } from "react";
import { FilterTwoTone, PlusCircleTwoTone } from "@ant-design/icons";
import throughShipmentApi, { InvoiceInfo, ExternalOrdersFilterParam } from "@api/throughShipment/throughShipmentApi";
import PageTitle from "@component/PageTitle";
import IntegratedTable from "@component/Table/IntegratedTable";
import { usePermission, StaffPermission } from "@hooks/usePermission";
import {
  externalOrderState,
  fetchExternalOrdersList,
  resetExternalOrderSlice,
  setExternalOrdersFilter,
  exportCSV,
} from "@redux/externalOrderSlice";
import { Button, Row, Modal, message } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/store";
import styled from "styled-components";
import CreateInvoiceModal from "./CreateInvoiceModal";
import ExternalOrderFilter from "./ExternalOrderFilter";

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;

const FlexBox = styled.div`
  display: flex;
  justify-content: flex-end;
  Button {
    margin-right: 10px;
  }
`;

export default function ExternalOrder() {
  const { externalOrdersListResult, externalOrdersFilter, isFetching } = useSelector(externalOrderState);

  const dispatch = useAppDispatch();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState<number | undefined>();
  const financeAccess = usePermission(StaffPermission.CAN_ACCESS_FINANCE_PAGE);

  const handelOnCreateInvoice = async (orderId: number, invoiceInfo: InvoiceInfo) => {
    try {
      await throughShipmentApi.fetchCreateInvoice(orderId, invoiceInfo);
      message.success("開立發票成功");
      dispatch(fetchExternalOrdersList(externalOrdersFilter));
      setShowInvoiceModal(undefined);
    } catch (e) {
      message.error("開立發票失敗");
    }
  };

  useEffect(() => {
    dispatch(fetchExternalOrdersList(externalOrdersFilter));
  }, [dispatch, externalOrdersFilter]);

  useEffect(() => {
    return () => {
      dispatch(resetExternalOrderSlice());
    };
  }, [dispatch]);

  const columns: ColumnsType<any> = [
    {
      key: "id",
      dataIndex: "id",
      title: "編號",
    },
    {
      key: "orderNumber",
      dataIndex: "orderNumber",
      title: "訂單編號",
    },
    {
      key: "approvalStatus",
      dataIndex: "approvalStatus",
      title: "審核狀態",
    },
    {
      key: "retailerName",
      dataIndex: "retailerName",
      title: "合作通路",
    },
    {
      key: "shippingStatus",
      dataIndex: "shippingStatus",
      title: "訂單狀態",
      render: (shippingStatus: string, values) =>
        values.returnOrderId ? (
          <Button type="link" onClick={() => window.open(`/return-product-management/${values.returnOrderId}`)}>
            已建立退貨單
          </Button>
        ) : (
          <div>{shippingStatus}</div>
        ),
    },
    {
      key: "staff",
      dataIndex: "staff",
      title: "訂單申請人",
      render: (_, values) => <div>{values?.staff?.name}</div>,
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "訂單建立時間",
      sorter: true,
    },
    {
      key: "shippingAt",
      dataIndex: "shippingAt",
      title: "倉庫建立時間",
    },
    {
      key: "deliveryAt",
      dataIndex: "deliveryAt",
      title: "實際出貨時間",
    },
    {
      key: "buttons",
      dataIndex: "buttons",
      title: "",
      render: (_, values) => {
        return (
          <Row justify="end">
            <Button type="link" onClick={() => window.open(`${window.location.href}/${values.id}/review`)}>
              檢視
            </Button>
            <Button
              type="link"
              onClick={() => window.open(`${window.location.href}/${values.id}`)}
              disabled={values.shippingStatus === "訂單處理中(已拋單)" || values.approvalStatus === "審核完成"}
            >
              編輯
            </Button>
            {values.deliveryAt && (
              <Button
                type="link"
                onClick={() => {
                  if (financeAccess) {
                    setShowInvoiceModal(values.id);
                  } else {
                    message.error("無權限開立發票");
                  }
                }}
                disabled={!values.canInvoice}
              >
                開立發票
              </Button>
            )}
          </Row>
        );
      },
    },
  ];

  const handleOnExport = () => {
    const filterValues = Object.values(externalOrdersFilter).filter((value) => {
      return value !== undefined;
    });

    if (filterValues.length === 2) {
      Modal.warning({
        title: "未選擇任何篩選條件",
        okText: "我知道了",
      });
    } else {
      const { limit, offset, ...exportFilters } = externalOrdersFilter;
      dispatch(exportCSV(exportFilters));
    }
  };

  return (
    <Wrapper>
      <PageTitle title="出貨訂單管理" />
      <Row justify="space-between">
        <FlexBox>
          <Button
            type="primary"
            icon={<PlusCircleTwoTone twoToneColor="#1890FF" />}
            onClick={() => window.open(`${window.location.href}/new`)}
          >
            建立出貨訂單
          </Button>
          <Button icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
            篩選
          </Button>
          <Button onClick={handleOnExport}>匯出</Button>
        </FlexBox>
      </Row>
      {showFilter && <ExternalOrderFilter />}
      <IntegratedTable
        isPagination
        paginationConfig={{
          totalCount: externalOrdersListResult.count,
          updateFilterParams: (filter: ExternalOrdersFilterParam) => dispatch(setExternalOrdersFilter(filter)),
          filterParams: externalOrdersFilter,
        }}
        loading={isFetching}
        scroll={{ x: "max-content" }}
        columns={columns}
        dataSource={externalOrdersListResult.results}
      />
      {showInvoiceModal && (
        <CreateInvoiceModal
          onClose={() => setShowInvoiceModal(undefined)}
          onSubmit={handelOnCreateInvoice}
          orderId={showInvoiceModal}
        />
      )}
    </Wrapper>
  );
}
